export const ROLE_SUBSCRIPTION_MANAGER = 'ROLE_SUBSCRIPTION_MANAGER';

/** Admin roles that can access Personal Trials (list + create). */
export const PERSONAL_TRIAL_ROLES = [
    'ROLE_SUPER_ADMIN',
    'ROLE_ADMIN',
    'ROLE_CORPORATE_ADMIN',
    ROLE_SUBSCRIPTION_MANAGER,
    'ROLE_CS_USER',
    'ROLE_CS_LTI_USER',
    'ROLE_PUBLISH',
] as const;

export const canManageSubscriptions = (roles: string[] = []): boolean =>
    roles.includes(ROLE_SUBSCRIPTION_MANAGER);

export const canCreatePersonalTrials = (roles: string[] = []): boolean =>
    roles.some((role) => (PERSONAL_TRIAL_ROLES as readonly string[]).includes(role));

/** Institution settings/users are read-only for CS and Subscription Manager (unless Super Admin). */
export const isInstitutionReadOnlyForUser = (roles: string[] = []): boolean => {
    if (roles.includes('ROLE_SUPER_ADMIN')) {
        return false;
    }

    return (
        roles.includes('ROLE_CS_USER') ||
        roles.includes('ROLE_CS_LTI_USER') ||
        roles.includes(ROLE_SUBSCRIPTION_MANAGER)
    );
};

/** Subscription tab is visible for institution editors, CS users, and Subscription Managers. */
export const canViewSubscriptionTab = (roles: string[] = []): boolean =>
    !isInstitutionReadOnlyForUser(roles) ||
    canManageSubscriptions(roles) ||
    roles.includes('ROLE_CS_USER') ||
    roles.includes('ROLE_CS_LTI_USER');

/**
 * Create/update FAM Prof-ISBN (faculty adoption).
 * Matches API `^/api/admin/.*` catch-all — CS may list professors/students but not mutate.
 */
export const FACULTY_ADOPTION_WRITE_ROLES = [
    'ROLE_SUPER_ADMIN',
    'ROLE_ADMIN',
    'ROLE_CORPORATE_ADMIN',
] as const;

export const canManageFacultyAdoption = (roles: string[] = []): boolean =>
    roles.some((role) => (FACULTY_ADOPTION_WRITE_ROLES as readonly string[]).includes(role));

/** Admins tab and admin creation stay limited to Super Admin. */
export const canAccessAdminSection = (roles: string[] = []): boolean => roles.includes('ROLE_SUPER_ADMIN');

/**
 * User creation (Users list + Add User).
 * CS employees use the CS roles. Account management (AM) employees use ROLE_ADMIN.
 */
export const USER_CREATION_ROLES = ['ROLE_CS_USER', 'ROLE_CS_LTI_USER', 'ROLE_ADMIN'] as const;

export const canAccessUserCreation = (roles: string[] = []): boolean =>
    canAccessAdminSection(roles) || roles.some((role) => (USER_CREATION_ROLES as readonly string[]).includes(role));

/** Roles CS and AM employees can assign. Super Admin keeps the full user-role list. */
export const LIMITED_USER_ROLE_VALUES = ['ROLE_PROFESSOR', 'ROLE_LIBRARIAN', 'ROLE_CAN_MANAGE_ACCOUNT'] as const;

const limitedUserRoleValues = new Set<string>(LIMITED_USER_ROLE_VALUES);

export const hasLimitedUserRoleOptions = (roles: string[] = []): boolean =>
    !canAccessAdminSection(roles) && roles.some((role) => (USER_CREATION_ROLES as readonly string[]).includes(role));

export const filterRolesForLimitedEditors = <T extends { value: string }>(roles: T[], viewerRoles: string[] = []): T[] => {
    if (!hasLimitedUserRoleOptions(viewerRoles)) {
        return roles;
    }

    return roles.filter((role) => limitedUserRoleValues.has(role.value));
};

/** Stored roles a CS/AM editor may reset or activate. Anything else is privileged. */
const ORDINARY_ACCOUNT_ROLES = new Set([
    'ROLE_USER',
    'ROLE_AUTHOR',
    'ROLE_LIBRARIAN',
    'ROLE_PROFESSOR',
    'ROLE_STUDENT',
    'ROLE_CAN_MANAGE_ACCOUNT',
    'ROLE_ALLOW_SITE_ACCESS',
    'ROLE_PI',
    'ROLE_CRM',
    'ROLE_CRM_LIBRARY',
    'ROLE_DRAFT',
    'ROLE_RECOMMENDATIONS',
    'ROLE_TRIALS',
    'ROLE_VIDEOGRAPHERS',
    'ROLE_CORE_SE_REVIEWERS',
]);

const isOrdinaryAccount = (roles: string[] = []): boolean => roles.every((role) => ORDINARY_ACCOUNT_ROLES.has(role));

export const canResetPasswordForUser = (actorRoles: string[] = [], targetRoles: string[] = []): boolean => {
    if (!canAccessUserCreation(actorRoles)) {
        return false;
    }

    if (canAccessAdminSection(actorRoles)) {
        return true;
    }

    return isOrdinaryAccount(targetRoles);
};

export const canChangeActiveStatusForUser = (actorRoles: string[] = [], targetRoles: string[] = []): boolean => {
    if (canAccessAdminSection(actorRoles)) {
        return true;
    }

    if (!hasLimitedUserRoleOptions(actorRoles)) {
        return false;
    }

    return isOrdinaryAccount(targetRoles);
};
